import React, { FC, useMemo } from 'react';

import { Box, Divider, Link, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import useSWR from 'swr';

import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import { NextImage } from '@components/atoms/image';
import ProductInstructorImageSkeleton from '@components/store/product/productInstructor/productInstructorInfo/productInstructorImageSkeleton';
import { getAssetUrl } from '@helpers/utils';
import { CourseInstructorEntity } from '@redux/course/type/courseDetailType';
import instructorApi, { INSTRUCTOR_ENDPOINT } from '@redux/instructor/api';
import { HttpFetchInstructorInfoApiResponse } from '@redux/instructor/type/instructorType';

const { fetchInstructorInfo } = instructorApi;

const MIN_REVIEW = 5;

type ProductInstructorInfoProps = {
  instructor: CourseInstructorEntity;
  isUnderline: boolean;
};

const ProductInstructorInfo: FC<ProductInstructorInfoProps> = ({ instructor, isUnderline }) => {
  const { t } = useTranslation('store_course');
  const theme = useTheme();

  const { data, isLoading } = useSWR<HttpFetchInstructorInfoApiResponse>(
    INSTRUCTOR_ENDPOINT.INFO(instructor.slug),
    () => fetchInstructorInfo({ slug: instructor.slug }),
  );

  const instructorInfoData = data?.data;

  const instructorCourseInfo = useMemo(() => {
    if (!instructorInfoData || instructorInfoData?.reviewTotal < MIN_REVIEW) {
      return [];
    }

    return [
      {
        icon: 'fa-light fa-star',
        text: t('course_detail.course_instructor.average_score', {
          score: (instructorInfoData?.reviewAverageRating ?? 0).toFixed(1),
        }),
      },
      {
        icon: 'fa-light fa-comment',
        text: `${instructorInfoData?.reviewTotal ?? 0} ${t('course_detail.course_instructor.review')}`,
      },
      {
        icon: 'fa-light fa-circle-play',
        text: t('course_detail.course_instructor.course', { course: instructorInfoData?.teachingCourseTotal ?? 0 }),
      },
    ];
  }, [instructorInfoData, t]);

  const biology = useMemo(() => {
    if (!instructor?.biology) {
      return null;
    }

    const biologyItems = instructor.biology.split('\n').map((biologyItem) => {
      return (
        <Typography key={biologyItem} component="li" variant="body1lanna" color={theme.palette.textV2.secondary}>
          <Box component="span" position="relative" top={-2} left={-6}>
            {biologyItem}
          </Box>
        </Typography>
      );
    });

    return biologyItems;
  }, [instructor, theme]);

  return (
    <Box mt={3}>
      <Box display="flex" gap={2} ml={2}>
        <Box width={40} minWidth={40} height={40} borderRadius="100%" overflow="hidden">
          {instructorInfoData || !isLoading ? (
            <NextImage
              src={instructorInfoData?.imageUrl || ''}
              alt={instructor.fullName}
              layout="responsive"
              width={40}
              height={40}
              errorImage={`${getAssetUrl('thumbnail-instructor.jpg')}`}
            />
          ) : (
            <ProductInstructorImageSkeleton />
          )}
        </Box>

        <Box>
          <Typography variant="h5" component="h6">
            {instructor.fullName}
          </Typography>

          {instructorCourseInfo.map((info) => {
            return (
              <Box
                key={info.text}
                display="grid"
                gridTemplateColumns="18px 1fr"
                alignItems="center"
                color={theme.palette.textV2.secondary}
              >
                <FontAwesomeIcon icon={info.icon} style={{ fontSize: 12 }} />
                <Typography variant="body2lanna">{info.text}</Typography>
              </Box>
            );
          })}

          <Link
            display="inline-block"
            width={74}
            underline="none"
            variant="button_deprecated"
            href={`/instructors/${instructor?.slug}`}
            py={0.5}
          >
            {t('course_detail.course_instructor.view_profile')}
          </Link>
        </Box>
      </Box>
      <Box mt={2} ml={2}>
        {biology}
      </Box>

      {isUnderline ? <Divider sx={{ mt: 3 }} /> : null}
    </Box>
  );
};

export default ProductInstructorInfo;
